import React, { useState, useContext } from "react";
import cancel from "../assets/images/navbar/cancel.png";
import arrow from "../assets/images/order/orange-arrow.png";
import { ProfileContext } from "../context/ProfileContext";
import { addressBookService, authService } from "../services";
import { GOOGLE_MAPS_API_KEY } from "../utils/constants";

type WelcomeProps = {
  isOpen: boolean;
  onClose: () => void;
};

const Welcome: React.FC<WelcomeProps> = ({ isOpen, onClose }) => {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const profileContext = useContext(ProfileContext);
  const updateProfile = profileContext?.updateProfile;
  const fetchProfile = profileContext?.fetchProfile;

  const categories: string[] = [
    "Pizza",
    "Burger",
    "Indian",
    "Chinese",
    "Thai",
    "Vegetarian",
    "Lunch",
    "Kebab",
    "Biryani",
    "Dosa",
    "Breakfast",
    "Non Veg",
  ];

  const getCurrentLocation = (): Promise<GeolocationPosition> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'));
        return;
      }
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      });
    });
  };

  const reverseGeocode = async (latitude: number, longitude: number): Promise<any> => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${GOOGLE_MAPS_API_KEY}`
      );
      const data = await response.json();
      if (data.status === 'OK' && data.results.length > 0) {
        return data.results[0];
      }
      throw new Error('Unable to get address from coordinates');
    } catch (error) {
      console.error('Reverse geocoding error:', error);
      throw error;
    }
  };

  const extractAddressComponents = (addressComponents: any[]) => {
    let city = '';
    let state = '';
    let pincode = '';
    let addressLine1 = '';
    let addressLine2 = '';
    
    addressComponents.forEach((component: any) => {
      if (component.types.includes('locality')) {
        city = component.long_name;
      } else if (component.types.includes('administrative_area_level_3') && !city) {
        city = component.long_name;
      }
      if (component.types.includes('administrative_area_level_1')) {
        state = component.long_name;
      }
      if (component.types.includes('postal_code')) {
        pincode = component.long_name;
      }
      if (component.types.includes('street_number') || component.types.includes('route')) {
        addressLine1 += (addressLine1 ? ' ' : '') + component.long_name;
      }
      if (component.types.includes('sublocality_level_1') || component.types.includes('sublocality')) {
        addressLine2 = component.long_name;
      }
    });
    
    return { city, state, pincode, addressLine1, addressLine2 };
  };

  const handleGetStarted = async () => {
    if (!fullName.trim()) {
      setError("Please enter your full name");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      // Split full name into first and last name
      const nameParts = fullName.trim().split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(' ') || firstName;

      // Get phone number from profile or token
      const profileResponse: any = await authService.getUserProfile();
      const phoneNumber = profileResponse?.data?.phoneNumber || "";

      // Update profile with name, email, and preferences
      if (updateProfile) {
        await updateProfile({
          firstName,
          lastName,
          email: email.trim() || undefined,
          preferences: selectedCategories,
        });
        console.log('Profile updated successfully');
      }

      // Get current location and create address
      try {
        const position = await getCurrentLocation();
        const { latitude, longitude } = position.coords;
        
        console.log('Current location:', { latitude, longitude });
        
        // Reverse geocode to get address details
        const geocodeResult = await reverseGeocode(latitude, longitude);
        const addressComponents = geocodeResult.address_components;
        const formattedAddress = geocodeResult.formatted_address;
        
        const { city, state, pincode, addressLine1, addressLine2 } = extractAddressComponents(addressComponents);
        
        console.log('Location details:', { city, state, pincode, addressLine1, addressLine2 });
        
        // Create address in address book
        const addressData = {
          fullName: fullName.trim(),
          phone: phoneNumber,
          addressLine1: addressLine1 || formattedAddress.split(',')[0],
          addressLine2: addressLine2 || 'Near me',
          city: city || 'Unknown',
          state: state || 'Unknown',
          pincode: pincode || '000000',
          geoLocation: {
            type: 'Point',
            coordinates: [longitude, latitude] as [number, number]
          },
          isDefault: true
        };

        await addressBookService.createAddress(addressData);
        console.log('Address created successfully');

        // Fetch updated profile
        if (fetchProfile) {
          await fetchProfile();
        }
      } catch (locationError) {
        console.error('Location error:', locationError);
        // Continue even if location fails
      }

      // Close modal
      onClose();
    } catch (err: any) {
      console.error("Error in onboarding:", err);
      setError(err.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleCategory = (category: string) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category],
    );
  };
  if (!isOpen) return null;
  return (
    <>
      {/* Backdrop Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 z-[1001] transition-colors duration-300"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="fixed inset-0 flex items-center justify-center z-[1002] pointer-events-none px-4">
        <div
          className="w-full max-w-[545px] p-6 md:p-8 rounded-[24px] bg-white dark:bg-gray-800 pointer-events-auto relative transition-colors duration-300 max-h-[90vh] overflow-y-auto"
          style={{
            boxShadow: "-23px 23px 72px 0 rgba(0, 0, 0, 0.15)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Content */}
          <div className="w-full mt-[24px] ">
            {/* welcome */}
            <div className="space-y-[8px]">
              <p className="text-[#050505] dark:text-white font-urbanist text-[32px] font-semibold leading-[125%]">
                Welcome to Duddus 🎉
              </p>
              <p className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-[16px] font-medium leading-[140%]">
                Enter these details for your preferences
              </p>
            </div>
            {/* Close Button */}
            <div
              className="absolute top-6 right-6 h-[36px] w-[36px] cursor-pointer border dark:border-gray-600 rounded-full p-[7px] bg-white dark:bg-gray-700 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              onClick={onClose}
            >
              <img src={cancel} alt="Close" className="h-[18px] w-[18px]" />
            </div>

            {/* Name Field */}
            <div className="my-[30px]">
              <label className="text-[#050505] dark:text-white font-urbanist text-[16px] font-medium leading-[140%] ">
                What should we call you?
              </label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full flex p-4 items-center gap-3 self-stretch rounded-[16px] border border-[#E5E5E5] dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white shadow-[0_1px_4px_0_rgba(229,229,229,0.40)] dark:shadow-none transition-colors mt-[12px]"
                placeholder="Enter your full name"
              />
            </div>

            {/* Email Field */}
            <div className="mb-[30px]">
              <label className="text-[#050505] dark:text-white font-urbanist text-[16px] font-medium leading-[140%]">
                Email (Optional)
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full flex p-4 items-center gap-3 self-stretch rounded-[16px] border border-[#E5E5E5] dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white shadow-[0_1px_4px_0_rgba(229,229,229,0.40)] dark:shadow-none transition-colors mt-[12px]"
                placeholder="Enter your email address"
              />
            </div>

            <div className="mb-[100px]">
              <h6 className="text-[#050505] dark:text-white font-urbanist text-[16px] font-medium leading-[140%] mb-[12px]">
                Select your preferences
              </h6>

              <div className="flex flex-wrap gap-3">
                {categories.map((item, index) => {
                  const isSelected = selectedCategories.includes(item);
                  return (
                    <button
                      key={index}
                      onClick={() => toggleCategory(item)}
                      className={`px-5 py-2 text-sm font-medium rounded-full border transition ${
                        isSelected
                          ? "bg-[#7604A5] dark:bg-purple-600 text-white border-[#7604A5] dark:border-purple-600"
                          : "border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                      }`}
                    >
                      {item}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <p className="text-red-500 font-urbanist text-[14px] mb-[16px] text-center">
                {error}
              </p>
            )}

            {/* Get Started Button */}
            <button
              onClick={handleGetStarted}
              disabled={isLoading}
              className="w-full flex items-center justify-between py-4 px-6 bg-[#7402A7] dark:bg-purple-600 rounded-[150px] cursor-pointer hover:bg-[#5f0289] dark:hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="text-white font-urbanist text-[20px] font-semibold leading-[140%]">
                {isLoading ? "Setting up..." : "Get Started"}
              </span>
              <div className="h-[42px] w-[42px] flex justify-center items-center bg-white rounded-full">
                <img
                  src={arrow}
                  alt="right arrow"
                  className="h-[41px] w-[30px] stroke-[2.587px] stroke-[#FB8606]"
                />
              </div>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Welcome;
